import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';
import {
  getLockState,
  saveLockState,
  clearLockState,
  updateLastActivity as updateActivityTimestamp,
  setLockedFlag,
  clearLockedFlag,
  isLockedFlagSet,
  consumeFreshAuthentication,
  APP_LOCKED_STORAGE_KEY,
} from '../lib/security';
import type { UserSecuritySettings } from '../types/security';

interface AppLockContextType {
  isLocked: boolean;
  isEnabled: boolean;
  hasPin: boolean;
  settings: UserSecuritySettings | null;
  lockApp: () => void;
  unlockApp: () => void;
  forceLogout: () => void;
  refreshSettings: () => Promise<void>;
  initializeLock: () => Promise<void>;
}

const AppLockContext = createContext<AppLockContextType | undefined>(undefined);

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
const ACTIVITY_THROTTLE = 2000; // Update activity every 2 seconds max

export function AppLockProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user, userData, signOut, loading: authLoading } = useAuth();
  // Read synchronously on load (medopd-docs 134): a locked screen must stay locked across a page refresh.
  // Before this, the lock lived only in React state and a refresh removed it without the PIN.
  const [isLocked, setIsLockedState] = useState<boolean>(() => isLockedFlagSet());
  /** Locking/unlocking always goes through here so the persisted flag can never disagree with the screen. */
  const setIsLocked = useCallback((locked: boolean) => {
    if (locked) setLockedFlag(); else clearLockedFlag();
    setIsLockedState(locked);
  }, []);
  const [settings, setSettings] = useState<UserSecuritySettings | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousUserIdRef = useRef<string | null>(null);

  // Check if app lock feature is available for current user role
  const isLockAvailable = userData?.role === 'clinic_admin' || userData?.role === 'doctor';

  console.log('🔐 AppLockContext - user:', user?.email, 'role:', userData?.role, 'isLockAvailable:', isLockAvailable);

  /**
   * Fetch security settings from backend
   */
  const refreshSettings = useCallback(async () => {
    console.log('🔐 refreshSettings called - isLockAvailable:', isLockAvailable, 'user:', user?.email);
    // Don't make API calls if no user is logged in or lock is not available
    if (!user || !isLockAvailable) {
      console.warn('🔐 refreshSettings aborted - no user or lock not available for this role');
      return;
    }

    try {
      const response = await api.security.getSettings();
      if (response.data) {
        console.log('🔐 Security settings loaded:', {
          hasPin: response.data.hasPin,
          lockEnabled: response.data.lockEnabled,
          autoLockTimeout: response.data.autoLockTimeout,
        });
        setSettings(response.data);
        // Update local storage
        saveLockState({
          isEnabled: response.data.lockEnabled,
          autoLockTimeout: response.data.autoLockTimeout,
          hasPin: response.data.hasPin,
        });
      }
    } catch (error) {
      console.error('Failed to fetch security settings:', error);
    }
  }, [isLockAvailable, user]);

  /**
   * Initialize lock on mount
   */
  const initializeLock = useCallback(async () => {
    // Don't initialize if no user or lock is not available
    if (!user || !isLockAvailable) {
      console.log('🔐 initializeLock skipped - no user or lock not available');
      return;
    }

    const currentUserId = userData?.id?.toString() || user?.email || '';
    previousUserIdRef.current = currentUserId;

    // A fresh login is ONLY a real sign-in in this tab (password / MFA), signalled by a one-shot marker.
    // It used to be "this ref differs from the user", but a page refresh resets the ref, so every refresh
    // counted as a fresh login and skipped the lock check (medopd-docs 134).
    const isFreshLogin = consumeFreshAuthentication();

    await refreshSettings();

    if (isFreshLogin) {
      // Signing in proved who the user is: any lock left from before is lifted.
      setIsLocked(false);
      updateActivityTimestamp();
      lastActivityRef.current = Date.now();
      console.log('✅ Fresh login detected - activity timestamp updated, skipping lock check');
      return;
    }

    // Page refresh / new tab: a screen that was locked stays locked until the PIN is entered.
    if (isLockedFlagSet()) {
      setIsLocked(true);
      return;
    }

    // This is a page refresh - check if should be locked BEFORE updating activity timestamp
    // This ensures page refresh doesn't bypass the lock
    const state = getLockState();
    if (state.isEnabled && state.hasPin) {
      const timeSinceActivity = (Date.now() - state.lastActivity) / 1000;
      if (timeSinceActivity >= state.autoLockTimeout) {
        console.log('🔒 App should be locked - time since activity:', Math.floor(timeSinceActivity), 'seconds');
        setIsLocked(true);
        // DON'T update activity timestamp - keep the lock
        return;
      }
    }

    // Only update activity timestamp if NOT being locked
    updateActivityTimestamp();
    lastActivityRef.current = Date.now();
    console.log('✅ App initialized - activity timestamp updated');
  }, [user, userData?.id, isLockAvailable, refreshSettings, setIsLocked]);

  /**
   * Lock the app
   */
  const lockApp = useCallback(() => {
    const state = getLockState();
    if (state.isEnabled && state.hasPin) {
      setIsLocked(true);
      console.log('🔒 App locked');
    }
  }, [setIsLocked]);

  /**
   * Unlock the app
   */
  /** Call only after the server has verified the PIN (LockScreen) or a PIN reset succeeded. */
  const unlockApp = useCallback(() => {
    setIsLocked(false);
    // Reset activity timestamp after unlock
    const now = Date.now();
    updateActivityTimestamp();
    lastActivityRef.current = now;
    console.log('🔓 App unlocked - activity timer reset');
  }, [setIsLocked]);

  /**
   * Force logout (after max failed attempts)
   */
  const forceLogout = useCallback(() => {
    clearLockState();
    signOut();
    navigate('/login');
    console.log('🚪 Force logout triggered');
  }, [signOut, navigate]);

  /**
   * Handle user activity (throttled)
   */
  const handleActivity = useCallback(() => {
    if (isLocked || !isLockAvailable) {
      return;
    }

    const now = Date.now();
    lastActivityRef.current = now;

    // Throttle activity updates
    if (activityThrottleRef.current) {
      return;
    }

    activityThrottleRef.current = setTimeout(() => {
      updateActivityTimestamp();
      activityThrottleRef.current = null;
    }, ACTIVITY_THROTTLE);
  }, [isLocked, isLockAvailable]);

  /**
   * The user's latest activity in ANY tab of this browser. Every tab records activity in shared storage;
   * judging inactivity only by this tab's own ref let an idle background tab lock — and, since locks are
   * shared between tabs, re-lock the tab in use every ~10 seconds after each unlock (medopd-docs 136).
   */
  const latestActivity = useCallback(
    () => Math.max(lastActivityRef.current, getLockState().lastActivity || 0),
    [],
  );

  /**
   * Check for inactivity and auto-lock
   */
  const checkInactivity = useCallback(() => {
    if (!isLockAvailable || isLocked) {
      return;
    }

    const state = getLockState();
    if (!state.isEnabled || !state.hasPin) {
      console.log('⏱️ Auto-lock disabled or no PIN configured');
      return;
    }

    const now = Date.now();
    const inactiveTime = (now - latestActivity()) / 1000; // seconds

    console.log(`⏱️ Inactivity check: ${Math.floor(inactiveTime)}s / ${state.autoLockTimeout}s`);

    if (inactiveTime >= state.autoLockTimeout) {
      console.log('🔒 Auto-lock triggered due to inactivity');
      lockApp();
    }
  }, [isLockAvailable, isLocked, lockApp, latestActivity]);

  /**
   * Handle page visibility change (lock when background)
   */
  const handleVisibilityChange = useCallback(() => {
    if (!isLockAvailable) {
      return;
    }

    if (document.hidden) {
      // App went to background - DON'T lock immediately
      // The regular inactivity check will handle locking after timeout
      console.log('📱 App went to background - inactivity timer will handle locking');
    } else {
      // App came to foreground
      console.log('📱 App came to foreground');

      // Check if should lock due to inactivity while in background
      const state = getLockState();
      if (state.isEnabled && state.hasPin) {
        const now = Date.now();
        const timeSinceLastActivity = (now - latestActivity()) / 1000;

        console.log(`⏱️ Time since last activity: ${Math.floor(timeSinceLastActivity)}s, timeout: ${state.autoLockTimeout}s`);

        if (timeSinceLastActivity >= state.autoLockTimeout) {
          console.log('🔒 Locking due to inactivity while in background');
          setIsLocked(true);
        } else {
          console.log(`⏱️ Not locking - only ${Math.floor(timeSinceLastActivity)}s inactive (need ${state.autoLockTimeout}s)`);
        }
      }
    }
  }, [isLockAvailable, setIsLocked, latestActivity]);

  /**
   * Setup activity listeners and inactivity timer
   */
  useEffect(() => {
    if (!isLockAvailable || !settings?.lockEnabled) {
      return;
    }

    console.log(`🔐 Auto-lock enabled with ${settings.autoLockTimeout}s timeout`);

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Setup inactivity check timer (check every 10 seconds)
    inactivityTimerRef.current = setInterval(checkInactivity, 10000);

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
      if (activityThrottleRef.current) {
        clearTimeout(activityThrottleRef.current);
      }
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    };
  }, [isLockAvailable, settings?.lockEnabled, handleActivity, handleVisibilityChange, checkInactivity]);

  /**
   * Initialize on mount and user change
   */
  useEffect(() => {
    if (userData && isLockAvailable) {
      initializeLock();
    } else if (userData && !isLockAvailable) {
      // Signed in with a role that has no app lock: nothing to keep locked.
      setSettings(null);
      setIsLocked(false);
    }
    // No user yet: either auth is still loading (page refresh) or the user signed out — the logout
    // effect below handles the latter. Clearing here is what used to drop the lock on every refresh.
  }, [userData, isLockAvailable, initializeLock, setIsLocked]);

  /**
   * Cleanup on logout
   */
  useEffect(() => {
    // Only once auth has finished loading: on a page refresh userData is briefly null while the session
    // is restored, and clearing then wiped the lock (and the activity time) on every refresh.
    if (!authLoading && !userData) {
      clearLockState();
      setSettings(null);
      setIsLocked(false);
      previousUserIdRef.current = null; // Reset previous user tracking
    }
  }, [authLoading, userData, setIsLocked]);

  /**
   * Other tabs: locking (or unlocking with the PIN) in one tab applies to every open tab of this browser,
   * so a second tab can't be used to get past a locked screen.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== APP_LOCKED_STORAGE_KEY) return;
      const locked = e.newValue === '1';
      // Unlocked with the PIN in another tab: that counts as activity here too, so this tab doesn't
      // judge itself idle by its own stale clock and lock everything again seconds later.
      if (!locked) lastActivityRef.current = Date.now();
      setIsLockedState(locked);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value: AppLockContextType = {
    isLocked,
    isEnabled: settings?.lockEnabled ?? false,
    hasPin: settings?.hasPin ?? false,
    settings,
    lockApp,
    unlockApp,
    forceLogout,
    refreshSettings,
    initializeLock,
  };

  return <AppLockContext.Provider value={value}>{children}</AppLockContext.Provider>;
}

export function useAppLock() {
  const context = useContext(AppLockContext);
  if (context === undefined) {
    throw new Error('useAppLock must be used within AppLockProvider');
  }
  return context;
}
